import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  useIsbtTemplates,
  useLabelPrintingMutations,
  useLabelTypes,
  useZebraTemplates,
} from '@/hooks/useLabelPrinting'
import {
  LABEL_TYPE_ROWS,
  PRINTER_FAMILIES,
  activePrintersText,
  availableConfigurations,
  labelTypeSavedText,
  printerFamilyLabel,
  type LabelType,
  type PrinterFamily,
} from '@/lib/labelPrinting'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ListSection } from './shared'

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

/**
 * Printer association (z8nrz7evkc): which kind of printer each print button sends its
 * label to. Four fixed rows, so no sorting or column settings. A manager's
 * change saves at once; on a failure the row shows the server's message and
 * keeps the type the server still holds.
 */
export function LabelTypesList({
  canManage,
  onNotice,
}: {
  canManage: boolean
  onNotice: (message: string) => void
}) {
  const query = useLabelTypes()
  // For the Configurations column; both lists are cached for their own views.
  const isbtTemplates = useIsbtTemplates()
  const zebraTemplates = useZebraTemplates()
  const { updateLabelType } = useLabelPrintingMutations()
  // The type each row is being saved as, shown until the save ends.
  const [saving, setSaving] = useState<Partial<Record<LabelType, PrinterFamily>>>({})
  const [errors, setErrors] = useState<Partial<Record<LabelType, string>>>({})

  async function change(labelType: LabelType, printerFamily: PrinterFamily) {
    setErrors(e => ({ ...e, [labelType]: undefined }))
    setSaving(s => ({ ...s, [labelType]: printerFamily }))
    try {
      await updateLabelType.mutateAsync({ labelType, printerFamily })
      onNotice(labelTypeSavedText(labelType, printerFamily))
    } catch (err) {
      setErrors(e => ({ ...e, [labelType]: errorText(err, 'Could not save the printer type.') }))
    } finally {
      setSaving(s => ({ ...s, [labelType]: undefined }))
    }
  }

  return (
    <ListSection
      title='Printer association'
      blurb='Choose which kind of printer each print button sends its label to. Print buttons follow this once label printing is switched on.'
      query={query}
      noun='printer associations'
      footnote='Faded sizes have no approved template yet, so no printer can use them.'
    >
      {rows => (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Print button</TableHead>
              <TableHead>Printer type</TableHead>
              <TableHead>Configurations</TableHead>
              <TableHead>Printers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LABEL_TYPE_ROWS.map(({ labelType, label, button }) => {
              const row = rows.find(r => r.labelType === labelType)
              if (!row) return null
              const pending = saving[labelType]
              const error = errors[labelType]
              const printers = activePrintersText(row.activePrinters, row.printerFamily)
              const configurations = availableConfigurations(
                pending ?? row.printerFamily,
                isbtTemplates.data ?? [],
                zebraTemplates.data ?? [],
              )
              return (
                <TableRow key={labelType}>
                  <TableCell className='font-medium'>{label}</TableCell>
                  <TableCell className='text-muted-foreground'>{button}</TableCell>
                  <TableCell>
                    {canManage ? (
                      <div className='flex flex-col gap-1'>
                        <div className='flex items-center gap-2'>
                          <Select
                            value={pending ?? row.printerFamily}
                            disabled={pending !== undefined}
                            onValueChange={v => void change(labelType, v as PrinterFamily)}
                          >
                            <SelectTrigger
                              className='h-9 w-48'
                              aria-label={`Printer type for ${label} labels`}
                              aria-describedby={error ? `label-type-${labelType}-error` : undefined}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRINTER_FAMILIES.map(f => (
                                <SelectItem key={f} value={f}>
                                  {printerFamilyLabel(f)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {pending !== undefined && (
                            <span role='status' className='flex items-center gap-1 text-xs text-muted-foreground'>
                              <Loader2 className='h-3.5 w-3.5 animate-spin' aria-hidden />
                              Saving…
                            </span>
                          )}
                        </div>
                        {error && (
                          <p id={`label-type-${labelType}-error`} role='alert' className='text-xs text-destructive'>
                            {error}
                          </p>
                        )}
                      </div>
                    ) : (
                      printerFamilyLabel(row.printerFamily)
                    )}
                  </TableCell>
                  <TableCell>
                    {configurations.length === 0 ? (
                      <span className='text-muted-foreground'>No templates yet</span>
                    ) : (
                      <ul className='flex flex-wrap gap-1.5' aria-label={`Label sizes for ${label} labels`}>
                        {configurations.map(c => (
                          <li
                            key={c.text}
                            className={cn(
                              'rounded-md border px-2 py-0.5 text-xs whitespace-nowrap',
                              c.ready ? 'bg-muted/40' : 'border-dashed text-muted-foreground',
                            )}
                            title={c.ready ? undefined : 'No approved template of this size yet'}
                          >
                            {c.text}
                          </li>
                        ))}
                      </ul>
                    )}
                  </TableCell>
                  <TableCell className={cn(printers.warn ? 'text-amber-700' : 'text-muted-foreground')}>
                    {printers.text}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </ListSection>
  )
}
